import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, PackagePlus, Plus, RefreshCw } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

import {
  crearItemCompras,
  getNextSerial,
  getTiposObjeto,
  type CrearItemComprasPayload,
  type CondicionElemento,
  type TipoObjeto,
} from '@/services/api/inventario.api';
import { getApiErrorMessage } from '@/services/api/client';
import { NuevoTipoObjetoDialog } from './NuevoTipoObjetoDialog';
import { cn } from '@/lib/utils';
import { CONDICION_META, CONDICIONES } from '@/lib/condicion';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ESTADO_INICIAL = {
  serial_global: '',
  tipo_objeto: '',
  especificacion: '',
  condicion: '' as CondicionElemento | '',
};

/**
 * Alta de un item del inventario de Compras (HU-4.2).
 *
 * Compras es un inventario independiente de GLPI — aquí se registran insumos
 * que NO son computadores (sillas, monitores, mouses, teclados, etc.).
 * Cada item se describe con:
 *   - Tipo de objeto: categoría del catálogo en BD (Silla, Mesa...). Se puede
 *     crear uno nuevo desde aquí con "+ Nuevo tipo".
 *   - Especificación:  detalle concreto (Silla Rimax, Silla de madera...). Sale
 *     de las especificaciones del tipo; si no tiene, es texto libre.
 */
export function AltaEquipoDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState(ESTADO_INICIAL);
  const [loadingSerial, setLoadingSerial] = useState(false);
  const [creandoTipo, setCreandoTipo] = useState(false);

  const tiposQuery = useQuery({
    queryKey: ['tipos-objeto'],
    queryFn: getTiposObjeto,
    enabled: open,
  });
  const tipos = tiposQuery.data ?? [];
  const opcionesEspecificacion =
    tipos.find((t) => t.nombre === form.tipo_objeto)?.especificaciones ?? [];

  function handleTipoCreado(tipo: TipoObjeto) {
    setForm((f) => ({ ...f, tipo_objeto: tipo.nombre, especificacion: '' }));
    void fetchNextSerial(tipo.nombre);
  }

  const setField = (campo: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  async function fetchNextSerial(tipo: string) {
    setLoadingSerial(true);
    try {
      const { serial } = await getNextSerial(tipo);
      setForm((f) => ({ ...f, serial_global: serial }));
    } catch {
      // silently ignore — user can type manually
    } finally {
      setLoadingSerial(false);
    }
  }

  const mutation = useMutation({
    mutationFn: (payload: CrearItemComprasPayload) => crearItemCompras(payload),
    onSuccess: (data) => {
      toast.success('Equipo registrado', {
        description: `${data.tipo_objeto} (${data.especificacion}) con serial ${data.serial_global} agregado a bodega.`,
      });
      qc.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      onOpenChange(false);
      setForm(ESTADO_INICIAL);
    },
    onError: (err) => {
      toast.error('No se pudo registrar el equipo', {
        description: getApiErrorMessage(err),
      });
    },
  });

  const serialValido = form.serial_global.trim().length > 0;
  const tipoValido = form.tipo_objeto.trim().length > 0;
  const especificacionValida = form.especificacion.trim().length > 0;
  const condicionValida = form.condicion !== '';
  const puedeGuardar =
    serialValido && tipoValido && especificacionValida && condicionValida && !mutation.isPending;

  function handleSubmit() {
    if (!puedeGuardar) return;
    mutation.mutate({
      serial_global: form.serial_global.trim(),
      tipo_objeto: form.tipo_objeto.trim(),
      especificacion: form.especificacion.trim(),
      condicion: form.condicion as CondicionElemento,
    });
  }

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <PackagePlus className="h-4 w-4" />
            </span>
            Registrar equipo de Compras
          </DialogTitle>
          <DialogDescription>
            Da de alta un insumo nuevo (silla, monitor, mouse, teclado, etc.). Quedará en bodega,
            listo para asignar.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="serial">
              Serial <span className="text-danger">*</span>
            </Label>
            <div className="relative">
              <Input
                id="serial"
                placeholder="Seleccioná el tipo para generar el serial"
                value={form.serial_global}
                onChange={setField('serial_global')}
                className="font-mono pr-8"
                autoFocus
              />
              {loadingSerial && (
                <RefreshCw className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="tipo">
                Tipo de objeto <span className="text-danger">*</span>
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                onClick={() => setCreandoTipo(true)}
              >
                <Plus className="h-3 w-3" />
                Nuevo tipo
              </Button>
            </div>
            <Select
              value={form.tipo_objeto}
              onValueChange={(v) => {
                setForm((f) => ({ ...f, tipo_objeto: v, especificacion: '' }));
                void fetchNextSerial(v);
              }}
            >
              <SelectTrigger id="tipo">
                <SelectValue
                  placeholder={tiposQuery.isLoading ? 'Cargando tipos...' : 'Seleccionar tipo'}
                />
              </SelectTrigger>
              <SelectContent>
                {tipos.length === 0 && !tiposQuery.isLoading && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No hay tipos. Creá uno con "Nuevo tipo".
                  </div>
                )}
                {tipos.map((t) => (
                  <SelectItem key={t.id_tipo} value={t.nombre}>
                    {t.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="especificacion">
              Especificación <span className="text-danger">*</span>
            </Label>
            {(() => {
              const opciones = opcionesEspecificacion;
              if (opciones.length > 0) {
                return (
                  <Select
                    value={form.especificacion}
                    onValueChange={(v) => setForm((f) => ({ ...f, especificacion: v }))}
                  >
                    <SelectTrigger id="especificacion">
                      <SelectValue placeholder="Seleccionar especificación" />
                    </SelectTrigger>
                    <SelectContent>
                      {opciones.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }

              return (
                <Input
                  id="especificacion"
                  placeholder="Ej: Silla Rimax, Silla de madera"
                  value={form.especificacion}
                  onChange={setField('especificacion')}
                />
              );
            })()}
          </div>

          <div className="grid gap-1.5">
            <Label>
              Estado del elemento <span className="text-danger">*</span>
            </Label>
            <div className="grid gap-1.5">
              {CONDICIONES.map((c) => {
                const meta = CONDICION_META[c];
                const Icon = meta.icon;
                const seleccionada = form.condicion === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, condicion: c }))}
                    className={cn(
                      'flex items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                      seleccionada
                        ? 'border-primary-400 bg-primary-50/60 ring-1 ring-primary-200'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-full border',
                        seleccionada ? 'border-primary-600' : 'border-slate-300',
                      )}
                    >
                      {seleccionada && <span className="h-2 w-2 rounded-full bg-primary-600" />}
                    </span>
                    <Icon className={cn('h-4 w-4 shrink-0', meta.text)} />
                    <span className="font-medium text-foreground">{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!puedeGuardar}>
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Registrando...
              </>
            ) : (
              'Registrar equipo'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <NuevoTipoObjetoDialog
      open={creandoTipo}
      onOpenChange={setCreandoTipo}
      onCreated={handleTipoCreado}
    />
    </>
  );
}
